import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons'
import type { Carer } from '@lmc/ts-common'
import { Avatar, Button } from '@lmc/ui-kit'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { getChipsForCarer } from '../../utils/chipMapping'
import { getCarerRole } from '../../utils/teamList'
import RolePermissionChips from './RolePermissionChips'
import RowActionsMenu from './RowActionsMenu'
import SiteAccessList from './SiteAccessList'

type TeamListItemV2Props = {
  testId?: string
  carer: Carer
  // Whether the viewer has the manage-users ability at all (CASL `update User`).
  // Single-site: the Edit button is hidden entirely when false.
  // Group: combined with canEditTarget to gate the RowActionsMenu item.
  canEditPermissions: boolean
  // Whether the viewer's privilege level allows editing THIS specific target.
  // Single-site: when canEditPermissions is true but canEditTarget is false,
  // the Edit button renders disabled with a "more senior role" tooltip.
  // Group: the RowActionsMenu hides the Edit item unless both flags are true.
  canEditTarget: boolean
  onEditPermissions: (carer: Carer) => void
  onSeeProfile: (carer: Carer) => void
  // Group-dashboard variant (CM-3388). When set, the row renders the
  // site-access column (in place of the add-on permission chips) and a 3-dot
  // actions menu (Edit permissions + Delete account) instead of the inline
  // Edit button. The single-site path leaves these unset and is unchanged.
  variant?: 'single-site' | 'group'
  siteNames?: string[]
  onDeleteAccount?: (carer: Carer) => void
  // Delete is gated separately from edit (CASL `delete User` vs `update User`),
  // so a viewer who can edit permissions but not delete sees no Delete action.
  canDeleteAccount?: boolean
}

const StyledRow = styled.li`
  list-style: none;
  background-color: ${(props) => props.theme.palette.white};
  border-top: 1px solid ${(props) => props.theme.color.neutral.color8};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 0;

  /* Mobile: collapse the columns into a vertical card (CM-3670). */
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    flex-wrap: nowrap;
  }
`

const StyledAccount = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 240px;
  padding: 12px 8px 12px 16px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    padding: 16px 16px 8px;
  }
`

const StyledIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 0 0;
`

const StyledName = styled.p`
  font-family: inherit;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.color.neutral.color3};
  margin: 0;
  word-break: break-word;
`

const StyledJobTitle = styled.p`
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.color.neutral.color4};
  margin: 0;
`

const StyledRoleAndAddOn = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1 0 0;
  min-width: 0;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    width: 100%;
  }
`

const StyledRole = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 138px;
  padding: 12px 8px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    padding: 8px 16px;
  }
`

const StyledMutedLabel = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.color.neutral.color4};
`

const StyledRoleValue = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.color.neutral.color3};
  margin: 0;
`

const StyledAddOn = styled.div`
  flex: 1 0 0;
  min-width: 127px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    padding: 8px 16px;
  }
`

const StyledSite = styled.div`
  flex: 1 0 0;
  min-width: 127px;
  padding: 12px 8px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    padding: 8px 16px;
  }
`

const StyledActions = styled.div<{ $singleSite: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 12px 8px;
  margin-left: auto;

  /* Tablet: stack the action buttons vertically on the right, each sized to its
     own text (not stretched to match the wider button) (CM-3670). */
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-direction: column;
    align-items: flex-end;
  }

  /* Mobile: actions span the card. Full-width buttons apply only to the
     single-site variant (Edit permissions / See profile); the group variant's
     compact RowActionsMenu icon trigger must keep its 32px size, so the width
     rule is scoped out of it. The ui-kit Button uses all:unset (box-sizing →
     content-box), so pin border-box to stop width:100% overflowing. */
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    margin-left: 0;
    padding: 8px 16px 16px;

    ${({ $singleSite }) =>
      $singleSite &&
      css`
        button {
          width: 100%;
          box-sizing: border-box;
        }
      `}
  }
`

const readJobTitle = (carer: Carer): string | undefined =>
  (carer as unknown as { jobTitle?: string }).jobTitle

const TeamListItemV2: React.FC<TeamListItemV2Props> = ({
  testId,
  carer,
  canEditPermissions,
  canEditTarget,
  onEditPermissions,
  onSeeProfile,
  variant = 'single-site',
  siteNames = [],
  onDeleteAccount,
  canDeleteAccount = false
}) => {
  const isGroupVariant = variant === 'group'
  const { t } = useTranslation(NAMESPACE.TEAM)
  // CM-4109: hide the Medication chip from services without eMAR.
  const { isEmar, isOutstanding } = useHomeType()
  const chips = getChipsForCarer(carer, { isEmar, isOutstanding })
  const rowTestId = testId ? `${testId}-${carer.id}` : undefined
  const picture = carer?.picture as unknown as string
  const fullName =
    `${carer?.name?.first ?? ''} ${carer?.name?.last ?? ''}`.trim()
  const jobTitle = readJobTitle(carer)

  return (
    <StyledRow data-testid={rowTestId}>
      <StyledAccount>
        <Avatar
          url={picture}
          size={24}
          data-testid={rowTestId ? `${rowTestId}-avatar` : undefined}
        />
        <StyledIdentity>
          <StyledName data-testid={rowTestId ? `${rowTestId}-name` : undefined}>
            {fullName}
          </StyledName>
          {jobTitle ? (
            <StyledJobTitle
              data-testid={rowTestId ? `${rowTestId}-job-title` : undefined}
            >
              {jobTitle}
            </StyledJobTitle>
          ) : null}
        </StyledIdentity>
      </StyledAccount>

      <StyledRoleAndAddOn>
        <StyledRole>
          <StyledMutedLabel>
            {t(reportsConstants.TEAM_LIST_PERMISSION_ROLE)}
          </StyledMutedLabel>
          <StyledRoleValue
            data-testid={rowTestId ? `${rowTestId}-role` : undefined}
          >
            {getCarerRole(carer?.role)}
          </StyledRoleValue>
        </StyledRole>
        {isGroupVariant ? (
          <StyledSite>
            <SiteAccessList
              siteNames={siteNames}
              testId={rowTestId ? `${rowTestId}-site-access` : undefined}
            />
          </StyledSite>
        ) : (
          <StyledAddOn>
            {chips.length > 0 && (
              <StyledMutedLabel>
                {t(reportsConstants.TEAM_LIST_ADDON)}
              </StyledMutedLabel>
            )}
            <RolePermissionChips
              chips={chips}
              testId={rowTestId ? `${rowTestId}-chips` : undefined}
            />
          </StyledAddOn>
        )}
      </StyledRoleAndAddOn>

      <StyledActions $singleSite={!isGroupVariant}>
        {isGroupVariant ? (
          <>
            <Button
              variant="default"
              onClick={() => onSeeProfile(carer)}
              data-testid={rowTestId ? `${rowTestId}-see-profile` : undefined}
            >
              {t(reportsConstants.TEAM_LIST_SEE_PROFILE)}
              <ArrowRightOutlined />
            </Button>
            {/* Group: hide the menu when neither action is available. The Edit
                item inside the menu is gated on both manage ability AND
                privilege-level (canEditPermissions && canEditTarget), while
                Delete is gated independently. */}
            {(canEditPermissions && canEditTarget) || canDeleteAccount ? (
              <RowActionsMenu
                carer={carer}
                onEditPermissions={onEditPermissions}
                onDeleteAccount={onDeleteAccount ?? (() => undefined)}
                canEditPermissions={canEditPermissions && canEditTarget}
                canDeleteAccount={canDeleteAccount}
                testId={rowTestId ? `${rowTestId}-actions-menu` : undefined}
              />
            ) : null}
          </>
        ) : (
          <>
            {/* Single-site: hide the Edit button entirely when the viewer has
                no manage ability; show disabled (with tooltip) only when the
                target is more senior than the viewer. */}
            {canEditPermissions && (
              <Button
                variant="default"
                icon={<EditOutlined />}
                onClick={() => onEditPermissions(carer)}
                disabledByPermission={!canEditTarget}
                permissionDisabledText={t(
                  reportsConstants.TEAM_LIST_EDIT_PERMISSIONS_DISABLED_TOOLTIP
                )}
                data-testid={
                  rowTestId ? `${rowTestId}-edit-permissions` : undefined
                }
              >
                {t(reportsConstants.TEAM_LIST_EDIT_PERMISSIONS)}
              </Button>
            )}
            <Button
              variant="default"
              onClick={() => onSeeProfile(carer)}
              data-testid={rowTestId ? `${rowTestId}-see-profile` : undefined}
            >
              {t(reportsConstants.TEAM_LIST_SEE_PROFILE)}
              <ArrowRightOutlined />
            </Button>
          </>
        )}
      </StyledActions>
    </StyledRow>
  )
}

export default TeamListItemV2
